﻿import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Slide } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Checkbox, TextBox } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import React, { useCallback, useContext, useEffect } from 'react';
import {
    CheckmarkSolid,
    CloseOutline,
    DragOutline,
    EditOutline,
    FileOutline,
    LinkOutline,
    TrashOutline,
    UploadSolid,
    WarningTriangleOutline
} from '../../../../../icons/icons';
import { getDirectoryDb } from '../../../../../services/directory-db';
import { checkGeojsonPathUnique } from '../../../../../services/geojson-validator';
import { UDViewGeojsonFilePath, UDViewGeojsonPath, UDViewGeojsonUrlPath } from '../../../../../udView/udview';
import WorkspaceItemContext from '../../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { patchFocusedWorkspace, patchFocusedWorkspaceView } from '../../../../state/store';
import { useFormStringValue } from '../../hooks/forms';
import { useViewCheckboxValue } from '../../hooks/view-forms';
import { GeojsonWarning } from '../../providers/CustomGeojsonProvider';

const MAX_GEOJSON_FILES = 3;

export default function CustomGeojsonSettings() {
    const dispatch = useAppDispatch();
    const workspace = useContext(WorkspaceItemContext);
    const geojsonView = useAppSelector((state) => state.workspace.items[workspace.id].view.staticData.customGeojson);
    const warnings = useAppSelector((state) => state.workspace.items[workspace.id].geojsonWarnings);
    const [isAddingUrl, setIsAddingUrl] = React.useState(false);
    const [pathIndexes, setPathIndexes] = React.useState(geojsonView.paths.map((_, index) => index));
    const [addError, setAddError] = React.useState<string | null>(null);
    const sensors = useSensors(useSensor(PointerSensor));

    const urlName = useFormStringValue('');
    const urlValue = useFormStringValue('');

    useEffect(() => {
        setPathIndexes(geojsonView.paths.map((_, index) => index));
    }, [geojsonView.paths]);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                setPathIndexes((prevIndexes) => {
                    const oldIndex = prevIndexes.indexOf(active.id as number);
                    const newIndex = prevIndexes.indexOf(over.id as number);
                    return arrayMove(prevIndexes, oldIndex, newIndex);
                });
                dispatch(
                    patchFocusedWorkspaceView((v) => {
                        v.staticData.customGeojson.paths = arrayMove(
                            v.staticData.customGeojson.paths,
                            active.id as number,
                            over.id as number
                        );
                    })
                );
                dispatch(
                    patchFocusedWorkspace((w) => {
                        w.geojsonWarnings = w.geojsonWarnings.map((warning) => {
                            if (warning.index === active.id) {
                                return { ...warning, index: over.id as number };
                            } else if (warning.index === over.id) {
                                return { ...warning, index: active.id as number };
                            }
                            return warning;
                        });
                    })
                );
            }
        },
        [dispatch]
    );

    const handleAddUrl = useCallback(() => {
        if (!urlValue.value || geojsonView.paths.length >= MAX_GEOJSON_FILES) {
            return;
        }
        const urlNameToUse =
            urlName.value || urlValue.value.split('/').filter(Boolean).slice(-1)[0].replace('.geojson', '').replace('.json', '');
        const newPath = { type: 'url', show: true, name: urlNameToUse, url: urlValue.value } satisfies UDViewGeojsonUrlPath;
        const duplicateError = checkGeojsonPathUnique(newPath, geojsonView.paths);
        if (duplicateError) {
            setAddError(duplicateError);
            return;
        }
        setAddError(null);
        dispatch(
            patchFocusedWorkspaceView((v) => {
                v.staticData.customGeojson.paths = [...v.staticData.customGeojson.paths, newPath];
            })
        );

        urlName.onChange({ value: '' } as any);
        urlValue.onChange({ value: '' } as any);
        setIsAddingUrl(false);
    }, [urlName.value, urlValue.value, geojsonView, dispatch]);

    //Uses file Api to read the file handle and name without loading the whole file into memory, the actual loading is done in the provider when the file is added to the view
    //saves the handle to indexed db
    const handleAddFile = useCallback(() => {
        if (geojsonView.paths.length >= MAX_GEOJSON_FILES) {
            return;
        }

        const openFile = async () => {
            try {
                const [fileHandle] = (await window.showOpenFilePicker({
                    types: [
                        {
                            description: 'GeoJSON files',
                            accept: { 'application/geo+json': ['.geojson', '.json'] }
                        }
                    ],
                    multiple: false
                })) as FileSystemFileHandle[];

                const permission = await fileHandle.queryPermission({ mode: 'read' });
                if (permission !== 'granted') {
                    const requestPermission = await fileHandle.requestPermission({ mode: 'read' });
                    if (requestPermission !== 'granted') {
                        console.warn('Permission to read the file was denied');
                        return;
                    }
                }

                const newPath = {
                    type: 'file',
                    show: true,
                    name: fileHandle.name.replace('.geojson', '').replace('.json', ''),
                    fileHandle: fileHandle.name
                } satisfies UDViewGeojsonPath;

                const duplicateError = checkGeojsonPathUnique(newPath, geojsonView.paths);
                if (duplicateError) {
                    setAddError(duplicateError);
                    return;
                }
                setAddError(null);

                const db = await getDirectoryDb();
                await db.put('file-handles', fileHandle, fileHandle.name);

                dispatch(
                    patchFocusedWorkspaceView((v) => {
                        v.staticData.customGeojson.paths = [...v.staticData.customGeojson.paths, newPath];
                    })
                );
            } catch (error) {
                console.error('Error picking file:', error);
            }
        };

        openFile();
    }, [geojsonView, dispatch]);

    return (
        <div className="d-flex flex-column gap-2 py-2 overflow-auto justify-content-between" style={{ width: '320px', minHeight: '350px' }}>
            <div className={'d-flex flex-column gap-1 px-3'}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToParentElement]}>
                    <SortableContext items={pathIndexes} strategy={verticalListSortingStrategy}>
                        {geojsonView.paths.map((path, index) => (
                            <PathCard pathModel={path} id={index} key={index} warning={warnings.find((w) => w.index === index) ?? null} />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
            {!geojsonView?.paths?.length && (
                <div className="text-secondary d-flex flex-column align-items-center gap-2">
                    <SvgIcon icon={FileOutline} size={'xlarge'} />
                    <div>No custom GeoJSON files added</div>
                </div>
            )}

            <div>
                <Slide direction={'up'} className={'w-100'}>
                    {isAddingUrl && (
                        <div className="d-flex flex-column gap-2 px-3 border-top py-2 secondary-bg-color">
                            <div className={'d-flex justify-content-between align-items-center'}>
                                <div className={'d-flex align-items-center gap-1'}>
                                    <SvgIcon icon={LinkOutline} size={'medium'} />
                                    <span>Add URL</span>
                                </div>
                                <Button
                                    svgIcon={CloseOutline}
                                    fillMode="flat"
                                    className="button-small"
                                    onClick={() => {
                                        setIsAddingUrl(false);
                                        setAddError(null);
                                    }}
                                />
                            </div>
                            <TextBox className="flex-grow-1" placeholder="Name (optional)" {...urlName} />
                            <div className={'d-flex gap-2'}>
                                <TextBox className="flex-grow-1" placeholder="https://..." {...urlValue} />
                                <Button fillMode="solid" themeColor={'primary'} onClick={() => handleAddUrl()}>
                                    Add
                                </Button>
                            </div>
                            {addError && (
                                <div className={'text-warning d-flex align-items-center gap-1'}>
                                    <SvgIcon icon={WarningTriangleOutline} size={'small'} />
                                    {addError}
                                </div>
                            )}
                        </div>
                    )}
                </Slide>
                <div className={'d-flex flex-column align-items-center gap-1 border-top pt-2 px-3'}>
                    <div className={'d-flex gap-2 flex-grow-1 w-100'}>
                        <Button
                            svgIcon={UploadSolid}
                            fillMode={'outline'}
                            className={'w-50'}
                            disabled={geojsonView.paths.length >= MAX_GEOJSON_FILES}
                            onClick={() => handleAddFile()}>
                            Local file
                        </Button>
                        <Button
                            svgIcon={LinkOutline}
                            fillMode={'outline'}
                            className={'w-50'}
                            disabled={geojsonView.paths.length >= MAX_GEOJSON_FILES}
                            onClick={() => setIsAddingUrl(!isAddingUrl)}>
                            URL
                        </Button>
                    </div>
                    {addError && !isAddingUrl && (
                        <div className={'text-warning d-flex align-items-center gap-1'}>
                            <SvgIcon icon={WarningTriangleOutline} size={'small'} />
                            {addError}
                        </div>
                    )}
                    {geojsonView.paths.length >= MAX_GEOJSON_FILES && (
                        <div className={'secondary-text'}>Maximum {MAX_GEOJSON_FILES} files reached</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PathCard({ pathModel, id, warning }: { pathModel: UDViewGeojsonPath; id: number; warning: GeojsonWarning | null }) {
    const dispatch = useAppDispatch();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const [isEditing, setIsEditing] = React.useState(false);

    const editNameBox = useFormStringValue(pathModel.name);
    const show = useViewCheckboxValue((v) => v.staticData.customGeojson.paths[id].show);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    const editName = useCallback(() => {
        if (!editNameBox.value) {
            return;
        }
        dispatch(
            patchFocusedWorkspaceView((v) => {
                v.staticData.customGeojson.paths[id].name = editNameBox.value;
            })
        );
        setIsEditing(false);
    }, [editNameBox.value, id, dispatch]);

    const handleDelete = useCallback(() => {
        dispatch(
            patchFocusedWorkspaceView((v) => {
                v.staticData.customGeojson.paths.splice(id, 1);
            })
        );
        dispatch(
            patchFocusedWorkspace((w) => {
                w.geojsonWarnings = w.geojsonWarnings.filter((warning) => warning.index !== id);
            })
        );
    }, [id, dispatch]);

    const handleRequestPermission = useCallback(async () => {
        const db = await getDirectoryDb();
        const handle = await db.get('file-handles', (pathModel as UDViewGeojsonFilePath).fileHandle);
        if (!handle || handle.kind !== 'file') {
            return;
        }

        await handle.requestPermission({ mode: 'read' });

        // Trigger a re-check by toggling show
        dispatch(
            patchFocusedWorkspaceView((v) => {
                v.staticData.customGeojson.paths[id].show = !v.staticData.customGeojson.paths[id].show;
            })
        );
        dispatch(
            patchFocusedWorkspaceView((v) => {
                v.staticData.customGeojson.paths[id].show = !v.staticData.customGeojson.paths[id].show;
            })
        );

        dispatch(
            patchFocusedWorkspace((w) => {
                w.geojsonWarnings = w.geojsonWarnings.filter((warning) => warning.index !== id);
            })
        );
    }, [pathModel, id, dispatch]);

    return (
        <div ref={setNodeRef} style={style}>
            <div className={clsx('d-flex flex-column gap-1 px-2 py-2 k-card', warning && 'card-warning')}>
                <div className={'d-flex justify-content-between align-items-center'}>
                    <div className={'d-flex align-items-center gap-1 overflow-hidden'}>
                        <span>
                            <SvgIcon icon={DragOutline} {...attributes} {...listeners} svgStyle={{ outline: 'none' }} />
                        </span>
                        <Checkbox {...show} />
                        <span>
                            <SvgIcon icon={pathModel.type === 'url' ? LinkOutline : FileOutline} size={'small'} />
                        </span>
                        {isEditing ? (
                            <TextBox {...editNameBox} fillMode={'outline'} />
                        ) : (
                            <span className={'text-nowrap text-truncate'}>{pathModel.name}</span>
                        )}
                    </div>
                    <div className={'d-flex align-items-center gap-1'}>
                        {isEditing ? (
                            <Button svgIcon={CheckmarkSolid} fillMode="flat" size={'small'} onClick={() => editName()} />
                        ) : (
                            <Button svgIcon={EditOutline} fillMode="flat" size={'small'} onClick={() => setIsEditing(true)} />
                        )}
                        <Button svgIcon={TrashOutline} fillMode="flat" size={'small'} onClick={() => handleDelete()} />
                    </div>
                </div>
                <div>
                    <div className={'secondary-text mx-3 text-nowrap text-truncate'}>
                        {pathModel.type === 'url' ? pathModel.url : pathModel.fileHandle}
                    </div>
                    {warning && (
                        <div className={'text-warning mx-3 d-flex flex-column gap-1'}>
                            <div className={'d-flex align-items-center gap-1'}>
                                <SvgIcon icon={WarningTriangleOutline} size={'small'} />
                                {warning.message}
                            </div>
                            {warning.permissionsMissing && pathModel.type === 'file' && (
                                <Button fillMode="outline" size={'small'} themeColor={'warning'} onClick={handleRequestPermission}>
                                    Grant access
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
